import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon } from './Icon';
import { Tooltip } from './Tooltip';
import { Text } from './Typography';
const Avatar = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: ${({ theme }) => theme.secondary.blendToBackground(50)};
  box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(150)};
  flex-shrink: 0;
  cursor: pointer;
  transition: box-shadow 0.2s;

  svg {
    --icon-size: 20px;
  }
`;
const ChevronButton = styled.button `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    border-color 0.2s,
    background 0.2s;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};

  svg {
    --icon-size: 20px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.secondary.blendToBackground(200)};
    background: ${({ theme }) => theme.secondary.blendToBackground(25)};
    color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  }

  &:active {
    background: ${({ theme }) => theme.secondary.blendToBackground(50)};
  }
`;
const CopyableId = styled.div `
  max-height: 16px;
  min-width: 0;
  cursor: pointer;
  transition: color 0.2s;
  overflow: hidden;
  flex: 1;

  &:hover ${Text} {
    color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  }

  ${Text} {
    transition: color 0.2s;
  }
`;
const Card = styled.div `
  box-shadow:
    0 -1px 1px
      color-mix(
        in srgb,
        ${({ theme }) => (theme.id === 'dark' ? `${theme.secondary.blendToBackground(300)}` : `${theme.background}`)}
          75%,
        transparent
      )
      inset,
    ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
      ${({ theme }) => theme.style.buttonSecondaryBorderWidth} ${({ theme }) => theme.style.buttonSecondaryBorderColor},
    0 1px 1px rgba(0, 0, 0, 0.075),
    0 1px 4px 0 transparent inset;
  border-radius: 8px;
  cursor: ${({ $clickable }) => $clickable && 'pointer'};
  padding: 12px;
  display: grid;
  grid-template-columns: ${({ $singleColumn }) => ($singleColumn ? 'auto' : '40px auto 32px')};
  align-items: center;
  gap: 12px;
  min-height: 64px;
  box-sizing: border-box;
  position: relative;

  &:hover:not(:has(${CopyableId}:hover)) ${Avatar} {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(300)};
  }

  &:hover:not(:has(${CopyableId}:hover)) ${ChevronButton} {
    border-color: ${({ theme }) => theme.secondary.blendToBackground(200)};
    background: ${({ theme }) => theme.secondary.blendToBackground(25)};
    color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  }
`;
const IDSuffix = styled(Text) `
  opacity: 0;
  max-width: 0;
  overflow: hidden;
  white-space: nowrap;
  flex-shrink: 0;
  transition:
    opacity 0.2s,
    max-width 0.2s;

  ${Card}:hover &,
  ${Card}:focus-within & {
    opacity: 1;
    max-width: 180px;
  }
`;
const NameText = styled(Text) `
  transition: color 0.2s;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  align-self: start;
  display: inline-flex;
  width: auto;

  &:hover {
    color: ${({ theme }) => theme.foreground};
  }
`;
const IDText = styled.div `
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
  width: 100%;
`;
const Metadata = styled.div `
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
  flex: 1;
`;
const ErrorContainer = styled.div `
  background-color: ${({ theme }) => theme.background};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  inset: 0;
  gap: 6px;
  opacity: 0;
  animation: fadeIn 0.3s ease-in forwards;
  border-radius: 8px;

  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }

  svg {
    --icon-color: ${({ theme }) => theme.danger.background};
    --icon-size: 20px;
  }
`;
const LoadingContainer = styled.div `
  background-color: ${({ theme }) => theme.background};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  inset: 0;
  opacity: 1;
  animation: ${({ $fadeOut }) => ($fadeOut ? 'fadeOut 0.3s ease-out forwards' : 'none')};
  border-radius: 8px;

  @keyframes fadeOut {
    to {
      opacity: 0;
    }
  }
`;
const TooltipWrapper = styled(Tooltip) `
  min-width: 0;
  flex: 1;
  display: flex;
`;
const Truncate = styled.span `
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;
const copyToClipboard = (text) => {
    if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
    }
    else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
    }
};
export const ObjectCard = ({ icon, primaryText, id, isLoading = false, error, onClick }) => {
    const [isTooltipShow, setIsTooltipShow] = useState(false);
    const [showCopied, setShowCopied] = useState(false);
    const [isTransitioningOut, setIsTransitioningOut] = useState(false);
    const prevIsLoadingRef = useRef(isLoading);
    useLayoutEffect(() => {
        if (prevIsLoadingRef.current && !isLoading) {
            setIsTransitioningOut(true);
        }
        prevIsLoadingRef.current = isLoading;
    }, [isLoading]);
    const handleLoadingAnimationEnd = () => {
        setIsTransitioningOut(false);
    };
    const isClickable = !isLoading && !isTransitioningOut && !error;
    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
    };
    const handleCopyId = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsTooltipShow(true);
        setShowCopied(true);
        copyToClipboard(id.value);
        setTimeout(() => {
            setIsTooltipShow(false);
            setTimeout(() => setShowCopied(false), 200);
        }, 550);
    };
    const IconComponent = icon && typeof Icon[icon] === 'function' ? Icon[icon] : Icon.Document;
    return (React.createElement(Card, { "$clickable": isClickable, onClick: isClickable ? handleClick : undefined },
        IconComponent && (React.createElement(Avatar, { as: "button", onClick: handleClick },
            React.createElement(IconComponent, null))),
        React.createElement(Metadata, null,
            React.createElement(NameText, { as: "button", onClick: handleClick }, primaryText),
            React.createElement(IDText, null,
                React.createElement(Text, { as: "p", size: 12, color: "lighter", weight: "medium", style: { flexShrink: 0, margin: 0 } }, id.label),
                React.createElement(TooltipWrapper, { content: showCopied ? 'Copied!' : id.value, isOpen: isTooltipShow },
                    React.createElement(CopyableId, { onClick: handleCopyId },
                        React.createElement(Text, { as: "p", size: 12, color: "lighter", monospace: true, style: { margin: 0 } },
                            React.createElement(Truncate, null, id.value)))),
                id.suffix ? (React.createElement(IDSuffix, { as: "p", size: 12, color: "light", style: { margin: 0 } }, id.suffix)) : null)),
        onClick && (React.createElement(ChevronButton, { type: "button", onClick: handleClick },
            React.createElement(Icon.ChevronRight, null))),
        (isLoading || isTransitioningOut) && (React.createElement(LoadingContainer, { "$fadeOut": isTransitioningOut, onAnimationEnd: handleLoadingAnimationEnd },
            React.createElement("span", null,
                React.createElement(Icon.Loading, null)))),
        error && (React.createElement(ErrorContainer, null,
            React.createElement(Icon.CircleCross, null),
            React.createElement(Text, { color: "danger" }, error)))));
};
//# sourceMappingURL=ObjectCard.js.map